import React from 'react';
import {createPortal} from 'react-dom';

// ============================================================================
// OverflowMenu — the trailing '⋯' (Design Constitution L10, L22, O06, §2)
// ----------------------------------------------------------------------------
// A 44px '⋯' button that opens a small menu of the actions a row or comment
// does not show inline (Edit, Delete, …). The menu sits on the menus layer
// (--z-menu, O01). Keyboard: Enter/Space/↓ open and focus the first item,
// ↑/↓ move, Home/End jump, Esc closes and returns focus to the button (Esc
// closes only this top-most layer, O06). A click outside closes it. A danger
// item reads in danger ink; it never runs a delete itself — the caller opens
// the §4 confirm. Layer, item hover and item focus paint live in index.html
// ([data-overflow-list], [data-overflow-item]).
//
// The list is portaled out of the row — into the open dialog element when the
// trigger sits in one, else <body> — and placed fixed from the trigger's box. A
// fixed child is not clipped by an ancestor's overflow, so a table's scroll
// wrapper, a card or the dialog panel never cuts it off, while staying inside
// the dialog keeps it in that dialog's semantics and focus trap. It opens upward
// when there is no room below (the last row of a list) and follows scrolling.
// ============================================================================

export type OverflowMenuItem = {
  key: string;
  label: React.ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  // Data hooks a screen's tests select by (data-*), kept when a row action moves into '⋯'.
  attrs?: Record<`data-${string}`, string>;
};

type OverflowMenuProps = {
  items: OverflowMenuItem[];
  label?: string;
  align?: 'left' | 'right';
};

const triggerStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid transparent',
  borderRadius: 999,
  background: 'transparent',
  color: 'var(--text-2)',
  fontFamily: 'inherit',
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
};

type MenuPosition = {top: number; left?: number; right?: number};

/** Where the list goes for a trigger box: below it, or above when it does not fit below. */
export function placeOverflowList(
  trigger: {top: number; bottom: number; left: number; right: number},
  list: {width: number; height: number},
  viewport: {width: number; height: number},
  align: 'left' | 'right',
): MenuPosition {
  const GAP = 4;
  const EDGE = 8;
  const fitsBelow = trigger.bottom + GAP + list.height <= viewport.height - EDGE;
  const fitsAbove = trigger.top - GAP - list.height >= EDGE;
  const top = !fitsBelow && fitsAbove ? trigger.top - GAP - list.height : trigger.bottom + GAP;
  // Keep the list on screen: a right-aligned list that would cross the left
  // edge aligns left instead, and the other way round.
  const rightFits = trigger.right - list.width >= EDGE;
  const leftFits = trigger.left + list.width <= viewport.width - EDGE;
  const side =
    align === 'right' ? (rightFits || !leftFits ? 'right' : 'left') : leftFits || !rightFits ? 'left' : 'right';
  return side === 'right'
    ? {top, right: Math.max(EDGE, viewport.width - trigger.right)}
    : {top, left: Math.max(EDGE, trigger.left)};
}

// The next enabled item from `at` moving by `step` (wrapping), or -1 if none.
export function nextEnabled(items: OverflowMenuItem[], at: number, step: 1 | -1): number {
  const n = items.length;
  for (let k = 1; k <= n; k++) {
    const i = (((at + step * k) % n) + n) % n;
    if (!items[i].disabled) return i;
  }
  return -1;
}

export default function OverflowMenu({items, label = 'More actions', align = 'right'}: OverflowMenuProps) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const [host, setHost] = React.useState<Element | null>(null);
  const [pos, setPos] = React.useState<MenuPosition | null>(null);
  const menuId = React.useId();

  const focusItem = (i: number) => {
    if (i >= 0) itemRefs.current[i]?.focus();
  };
  const close = (returnFocus: boolean) => {
    setOpen(false);
    setPos(null);
    if (returnFocus) triggerRef.current?.focus();
  };

  React.useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open]);

  // Place the list from the trigger's box; re-place on scroll (any scroller) and resize.
  React.useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      setPos(
        placeOverflowList(
          r,
          {width: listRef.current?.offsetWidth ?? 0, height: listRef.current?.offsetHeight ?? 0},
          {width: window.innerWidth, height: window.innerHeight},
          align,
        ),
      );
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, align, host]);

  const openAndFocus = (at: number) => {
    // Inside a dialog the list stays within that dialog, so it keeps the
    // dialog's semantics (aria-modal) and its focus trap; elsewhere <body>.
    const t = triggerRef.current;
    setHost(t?.closest('[role="dialog"]') ?? (typeof document !== 'undefined' ? document.body : null));
    setOpen(true);
    requestAnimationFrame(() => focusItem(at));
  };

  return (
    <div
      ref={rootRef}
      data-overflow-menu="1"
      // A '⋯' on a clickable row: opening the menu or picking an item must not
      // also open the row's record (L10).
      onClick={(e) => e.stopPropagation()}
      style={{position: 'relative', display: 'inline-block'}}
    >
      <button
        ref={triggerRef}
        type="button"
        className="wcf-clickable"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-overflow-trigger="1"
        onClick={() => (open ? close(false) : openAndFocus(nextEnabled(items, -1, 1)))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            openAndFocus(nextEnabled(items, -1, 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            openAndFocus(nextEnabled(items, 0, -1));
          }
        }}
        style={triggerStyle}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open &&
        portalTo(
          host,
          <div
            ref={listRef}
            id={menuId}
            role="menu"
            aria-label={label}
            data-overflow-list="1"
            onKeyDown={(e) => {
              const at = itemRefs.current.findIndex((el) => el === document.activeElement);
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                close(true);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                focusItem(nextEnabled(items, at, 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                focusItem(nextEnabled(items, at < 0 ? 0 : at, -1));
              } else if (e.key === 'Home') {
                e.preventDefault();
                focusItem(nextEnabled(items, -1, 1));
              } else if (e.key === 'End') {
                e.preventDefault();
                focusItem(nextEnabled(items, 0, -1));
              } else if (e.key === 'Tab') {
                // Tab dismisses the menu and returns focus to the '⋯' button,
                // the way a menu normally closes. It is consumed here: letting
                // it through would reach the dialog's focus trap while the
                // closing list is still in the DOM, so the trap would measure
                // the wrong last element and let focus leave the dialog.
                e.preventDefault();
                e.stopPropagation();
                close(true);
              }
            }}
            style={{
              position: 'fixed',
              ...(pos ?? {top: 0, [align]: 0}),
              // Hidden until placed, so it never flashes at the corner.
              visibility: pos ? 'visible' : 'hidden',
              minWidth: 180,
              padding: 6,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              boxShadow: 'var(--shadow-hover)',
            }}
          >
            {items.map((item, i) => (
              <button
                {...item.attrs}
                key={item.key}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                data-overflow-item={item.key}
                onClick={() => {
                  if (item.disabled) return;
                  close(true);
                  item.onSelect();
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  minHeight: 44,
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: 10,
                  background: 'transparent',
                  color: item.danger ? 'var(--danger-ink)' : 'var(--ink)',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'left',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.45 : 1,
                }}
              >
                {item.label}
              </button>
            ))}
          </div>,
        )}
    </div>
  );
}

function portalTo(host: Element | null, node: React.ReactElement): React.ReactNode {
  return host ? createPortal(node, host) : node;
}
